import { useState } from "react";
import { Box, Typography, Tooltip, Divider, useMediaQuery } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import HistoryIcon from "@mui/icons-material/History";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import { useLocation, useHistory } from "react-router-dom";
import FeedbackDialog from "../FeedbackDialog";
import { scheduleTour } from "../../utils/walkthroughTrigger";
import { SHORTCUTS_EVENT, KEYBOARD_UI_QUERY } from "../KeyboardShortcuts";

const EXPANDED_W = 220;
const COLLAPSED_W = 56;
const LS_KEY = "hw-sidebar-collapsed";

const primary   = "#1535BD";
const activeBg  = "rgba(21,53,189,0.08)";
const dimColor  = "#94a3b8";

const NAV_ITEMS = [
  { label: "Home",       path: "/",           icon: <HomeOutlinedIcon sx={{ fontSize: 19 }} /> },
  { label: "Flashcards", path: "/flashcards", icon: <StyleOutlinedIcon sx={{ fontSize: 19 }} /> },
  { label: "Bookmarks",  path: "/bookmarks",  icon: <BookmarkBorderIcon sx={{ fontSize: 19 }} /> },
  { label: "History",    path: "/history",    icon: <HistoryIcon sx={{ fontSize: 19 }} /> },
];

const itemSx = (active, collapsed) => ({
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  px: collapsed ? 0 : 1.25,
  py: 0,
  width: collapsed ? 36 : "100%",
  height: 34,
  mx: collapsed ? "auto" : 0,
  justifyContent: collapsed ? "center" : "flex-start",
  borderRadius: "8px",
  mb: 0.5,
  cursor: "pointer",
  userSelect: "none",
  color: active ? primary : dimColor,
  bgcolor: active ? activeBg : "transparent",
  transition: "background-color 0.15s, color 0.15s",
  "&:hover": {
    bgcolor: active ? "rgba(21,53,189,0.12)" : "rgba(0,0,0,0.05)",
    color: active ? primary : "#475569",
  },
});

const NavItem = ({ label, path, icon, collapsed, location, history }) => {
  const active = location.pathname === path;
  const btn = (
    <Box sx={itemSx(active, collapsed)} onClick={() => history.push(path)}>
      <Box sx={{ display: "flex", flexShrink: 0, color: "inherit" }}>{icon}</Box>
      {!collapsed && (
        <Typography sx={{
          fontSize: "0.8rem",
          fontWeight: active ? 700 : 500,
          fontFamily: "Mulish, sans-serif",
          color: "inherit",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}>
          {label}
        </Typography>
      )}
    </Box>
  );
  return collapsed ? <Tooltip title={label} placement="right" key={path}>{btn}</Tooltip> : btn;
};

const BottomItem = ({ label, icon, onClick, collapsed, id, active }) => {
  const btn = (
    <Box id={id} sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      px: collapsed ? 0 : 1.25,
      py: 0,
      width: collapsed ? 36 : "100%",
      height: 30,
      mx: collapsed ? "auto" : 0,
      justifyContent: collapsed ? "center" : "flex-start",
      borderRadius: "8px",
      mb: 0.25,
      cursor: "pointer",
      userSelect: "none",
      color: active ? primary : dimColor,
      bgcolor: active ? activeBg : "transparent",
      transition: "background-color 0.15s, color 0.15s",
      "&:hover": { bgcolor: active ? "rgba(21,53,189,0.12)" : "rgba(0,0,0,0.05)", color: active ? primary : "#475569" },
    }} onClick={onClick}>
      <Box sx={{ display: "flex", flexShrink: 0, color: "inherit" }}>{icon}</Box>
      {!collapsed && (
        <Typography sx={{
          fontSize: "0.75rem",
          fontWeight: 500,
          fontFamily: "Mulish, sans-serif",
          color: "inherit",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}>
          {label}
        </Typography>
      )}
    </Box>
  );
  return collapsed ? <Tooltip title={label} placement="right">{btn}</Tooltip> : btn;
};

const DesktopSidebar = () => {
  const location = useLocation();
  const history  = useHistory();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(LS_KEY) === "true"
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // The sidebar also shows on phones in landscape, which have no keyboard.
  const showShortcuts = useMediaQuery(KEYBOARD_UI_QUERY);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(LS_KEY, String(next));
    document.documentElement.style.setProperty(
      "--hw-sidebar-w",
      next ? `${COLLAPSED_W}px` : `${EXPANDED_W}px`
    );
  };

  return (
    <>
      <Box sx={{
        display: { xs: "none", sm: "flex" },
        flexDirection: "column",
        position: "fixed",
        top: 0,
        left: 0,
        width: collapsed ? COLLAPSED_W : EXPANDED_W,
        height: "100vh",
        bgcolor: "white",
        borderRight: "1px solid rgba(0,0,0,0.07)",
        zIndex: 200,
        pt: 2.5,
        pb: 2,
        px: collapsed ? "10px" : 2,
        overflowX: "hidden",
        overflowY: "auto",
        userSelect: "none",
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), padding 0.25s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {/* Logo */}
        {collapsed ? (
          <Tooltip title="Hans Wehr" placement="right">
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3.5, cursor: "pointer" }} onClick={() => history.push("/")}>
              <img src="/assets/book.png" alt="" style={{ width: 32, height: 32, objectFit: "contain" }} />
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 3.5, px: 1.25, cursor: "pointer" }} onClick={() => history.push("/")}>
            <img src="/assets/book.png" alt="" style={{ width: 32, height: 32, objectFit: "contain" }} />
            <Typography sx={{
              fontWeight: 800,
              fontSize: "0.9rem",
              color: primary,
              letterSpacing: "-0.02em",
              fontFamily: "Mulish, sans-serif",
              whiteSpace: "nowrap",
            }}>
              Hans Wehr
            </Typography>
          </Box>
        )}

        {/* Nav items */}
        {NAV_ITEMS.map(({ label, path, icon }) => (
          <NavItem
            key={path}
            label={label}
            path={path}
            icon={icon}
            collapsed={collapsed}
            location={location}
            history={history}
          />
        ))}

        {/* Bottom section */}
        <Box sx={{ mt: "auto" }}>
          <Divider sx={{ mb: 1.5, opacity: 0.4 }} />
          <BottomItem
            id="wt-sidebar-tour"
            label="Guided tour"
            icon={<HelpOutlineIcon sx={{ fontSize: 17 }} />}
            onClick={() => {
            if (location.pathname === "/") {
              window.dispatchEvent(new CustomEvent("hw-start-tour"));
            } else {
              scheduleTour();
              history.push("/");
            }
          }}
            collapsed={collapsed}
          />
          {showShortcuts && (
            <BottomItem
              id="wt-sidebar-shortcuts"
              label="Keyboard shortcuts"
              icon={<KeyboardIcon sx={{ fontSize: 17 }} />}
              onClick={() => window.dispatchEvent(new CustomEvent(SHORTCUTS_EVENT))}
              collapsed={collapsed}
            />
          )}
          <BottomItem
            label="Feedback"
            icon={<ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />}
            onClick={() => setFeedbackOpen(true)}
            collapsed={collapsed}
          />
          <BottomItem
            label="Privacy Policy"
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 17 }} />}
            onClick={() => history.push("/privacy-policy")}
            collapsed={collapsed}
          />
          <BottomItem
            label="Settings"
            icon={<SettingsOutlinedIcon sx={{ fontSize: 17 }} />}
            onClick={() => history.push("/settings")}
            collapsed={collapsed}
            active={location.pathname === "/settings"}
          />

          <Divider sx={{ my: 1.5, opacity: 0.4 }} />

          {/* Collapse toggle */}
          <Tooltip title={collapsed ? "Expand" : ""} placement="right">
            <Box
              onClick={toggle}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: collapsed ? 0 : 1.25,
                py: 0,
                width: collapsed ? 36 : "100%",
                height: 30,
                mx: collapsed ? "auto" : 0,
                justifyContent: collapsed ? "center" : "flex-start",
                borderRadius: "8px",
                cursor: "pointer",
                color: dimColor,
                transition: "background-color 0.15s, color 0.15s",
                "&:hover": { bgcolor: "rgba(0,0,0,0.05)", color: "#475569" },
              }}
            >
              {collapsed
                ? <ChevronRightIcon sx={{ fontSize: 18 }} />
                : (
                  <>
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      fontFamily: "Mulish, sans-serif",
                      color: "inherit",
                      whiteSpace: "nowrap",
                    }}>
                      Collapse
                    </Typography>
                  </>
                )
              }
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} mode="general" />
    </>
  );
};

export default DesktopSidebar;
